import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import autoBind from 'react-autobind';
import sortBy from 'lodash-es/sortBy';
import classNames from 'classnames/bind';

import withComponentName from '../../../../decorators/withComponentName';
import PresentationsSvc from '../../../../services/dbServices/PresentationsSvc';

import Preloader from '../../../Preloader';
import AllTypesReport from './AllTypesReport';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

class TypesSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      isTypeLoading: false,
      availableTypes: [],
    };

    autoBind(this);
    this.typesSectionRef = createRef();
    this.mounted = true;
  }

  async componentDidMount() {
    await this.generateData();
    window.addEventListener('load', this.resizeAllMasonryItems, { passive: true });
    window.addEventListener('resize', this.resizeAllMasonryItems, { passive: true });
    this.resizeAllMasonryItems();
  }

  componentDidUpdate(prevProps) {
    const { identifierProps, isSingleTopic } = this.props;
    if (identifierProps !== prevProps.identifierProps) {
      this.generateData();
    }
    if (isSingleTopic !== prevProps.isSingleTopic) {
      this.resizeAllMasonryItems();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('load', this.resizeAllMasonryItems);
    window.removeEventListener('resize', this.resizeAllMasonryItems);
  }

  callAPI(params) {
    const { isEsg } = this.props;

    if (isEsg) {
      return PresentationsSvc.getPresentationsEsgData(params)
        .then(({ data }) => data || {})
        .catch(() => { });
    }
    return PresentationsSvc.getPresentationsData(params)
      .then(({ data }) => data || {})
      .catch(() => { });
  }

  async generateData() {
    const { identifierProps, isEsg } = this.props;
    const params = {
      group0: 'type',
      group: 'year',
      identifiers: identifierProps,
    };
    this.setState({
      isTypeLoading: true,
    });
    const data = await this.callAPI(params);
    if (!this.mounted) {
      return;
    }
    const annualType = [];
    const quarterlyType = [];
    const typesArr = [];

    for (let i = 0; i < Object.keys(data).length; i += 1) {
      const currentType = Object.keys(data)[i];
      const currentData = data[currentType];
      const availableYears = sortBy((currentData || []).filter((f) => f)).reverse();
      const hasOthersData = (isEsg && (currentData || []).filter((f) => !f).length)
        || currentType.toLowerCase().includes('others');

      if (
        availableYears.length
        || currentType.toLowerCase().includes('others')
        || isEsg
      ) {
        const typeData = {
          id: i + 1,
          title: currentType,
          type: currentType,
          yearsOptions: availableYears,
          firstSelectedYear: availableYears[0],
          hasNoYearFilters: !availableYears.length && (currentType.toLowerCase().includes('others') || isEsg),
          hasOthersData,
        };
        if (currentType.toLowerCase().includes('annual')) {
          annualType.push(typeData);
        } else if (currentType.toLowerCase().includes('quarterly')) {
          quarterlyType.push(typeData);
        } else {
          typesArr.push(typeData);
        }
      }
    }
    this.setState({
      availableTypes: [...annualType, ...quarterlyType, ...typesArr],
      isLoading: false,
      isTypeLoading: false,
    }, () => {
      this.resizeAllMasonryItems();
    });
  }

  resizeMasonryItem(item) {
    const { width } = this.props;
    if (width <= 767) return;

    /**
     * Set appropriate spanning to any masonry item
     *
     * Get different properties we already set for the masonry, calculate
     * height or spanning for any cell of the masonry grid based on its
     * content-wrapper's height, the (row) gap of the grid, and the size
     * of the implicit row tracks.
     *
     * @param item Object A brick/tile/cell inside the masonry
    */

    /* Get the grid object, its row-gap, and the size of its implicit rows */
    const grid = this.typesSectionRef.current;
    const rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-row-gap'), 10);
    const rowHeight = parseInt(window.getComputedStyle(grid).getPropertyValue('grid-auto-rows'), 10);

    /*
     * Spanning for any brick = S
     * Grid's row-gap = G
     * Size of grid's implicitly create row-track = R
     * Height of item content = H
     * Net height of the item = H1 = H + G
     * Net height of the implicit row-track = T = G + R
     * S = H1 / T
    */
    const rowSpan = Math.ceil(
      (item.getBoundingClientRect().height + rowGap) / (rowHeight + rowGap),
    );

    /* Set the spanning as calculated above (S) */
    // eslint-disable-next-line no-param-reassign
    item.style.gridRowEnd = `span ${rowSpan}`;
  }

  resizeAllMasonryItems() {
    const { width } = this.props;
    if (width <= 767) return;

    /**
     * Apply spanning to all the masonry items
     *
     * Loop through all the items and apply the spanning to them using
     * `resizeMasonryItem()` function.
     *
     * @uses resizeMasonryItem
    */

    // Get all item class objects in one list
    const allItems = this.typesSectionRef.current?.childNodes;
    if (!allItems) {
      return;
    }

    /*
     * Loop through the above list and execute the spanning function to
     * each list-item (i.e. each masonry item)
     */
    for (let i = 0; i < allItems.length; i += 1) {
      this.resizeMasonryItem(allItems[i]);
    }
  }

  render() {
    const {
      identifierProps,
      isWatchlists,
      companyLists,
      isSingleTopic,
      isEsg,
      isIR,
    } = this.props;
    const { availableTypes, isLoading, isTypeLoading } = this.state;

    return (
      <div
        className={cx('types_section', { watchlists: isWatchlists, esg: isEsg, ir: isIR })}
        ref={this.typesSectionRef}
      >
        <Preloader loading={isLoading} top="70px" />
        {availableTypes.map(({
          id, title, type, yearsOptions, firstSelectedYear, hasNoYearFilters, hasOthersData,
        }) => (
          <AllTypesReport
            key={id}
            title={title}
            type={type}
            yearsOptions={yearsOptions}
            firstSelectedYear={firstSelectedYear}
            identifierProps={identifierProps}
            resizeMasonryItem={this.resizeMasonryItem}
            isWatchlists={isWatchlists}
            companyLists={companyLists}
            isSingleTopic={isSingleTopic}
            isEsg={isEsg}
            hasNoYearFilters={hasNoYearFilters}
            hasOthersData={hasOthersData}
            isTypeLoading={isTypeLoading}
            isIR={isIR}
          />
        ))}
      </div>
    );
  }
}

const mapStateToProps = ({ common }) => ({
  width: common.width,
});

export default withComponentName(connect(mapStateToProps, {})(TypesSection));
